import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import http from "@/lib/api";
import { toast } from "sonner";
import { Panel, Table, Td, Empty, fmt } from "./AdminKit";

const PROBLEM_LABEL = {
  translation_failed: "Перевод не прошёл проверку — письмо НЕ отправлено",
  failed: "Письмо НЕ доставлено",
  delivery_unknown: "Результат отправки НЕИЗВЕСТЕН (сбой сервера) — проверьте у клиента, прежде чем отправлять повторно",
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const isProblem = (r) => r.trigger_type !== "chat_otp" && !r.acknowledged_at && PROBLEM_LABEL[r.status];

function ProblemEmail({ r, onDone }) {
  const [to, setTo] = useState(r.recipient_email || "");
  const [subject, setSubject] = useState(r.subject || r.subject_no || "");
  const [body, setBody] = useState(r.body || r.body_no || "");
  const [busy, setBusy] = useState(false);
  const validTo = EMAIL_RE.test(to.trim());
  const act = async (path, payload, okMsg) => {
    setBusy(true);
    try {
      const res = await http.post(`/v1/admin/notifications/${r.id}/${path}`, payload);
      if (res.data?.status === "failed") toast.error("Отправка снова не удалась — проверьте адрес"); else toast.success(okMsg);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ошибка"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-amber-700/60 bg-amber-950/20 p-3 space-y-2 text-sm" data-testid={`held-email-${r.id}`}>
      <div className="text-amber-300 font-medium" data-testid={`held-email-label-${r.id}`}>{PROBLEM_LABEL[r.status]}</div>
      <div className="text-xs text-slate-400">
        {fmt(r.created_at)} · {r.trigger_type || "—"} · {r.job_code || "без заказа"}{r.target_language ? ` · язык ${r.target_language.toUpperCase()}` : ""}
        {r.retry_count ? ` · повторов: ${r.retry_count}` : ""}
      </div>
      {r.error_message && <div className="text-xs text-red-300" data-testid={`held-email-error-${r.id}`}>Причина: {r.error_message}</div>}
      <label className="block text-xs text-slate-400">Получатель (проверьте адрес)
        <input value={to} onChange={(e) => setTo(e.target.value)} data-testid={`held-email-to-${r.id}`}
          className={`mt-1 w-full rounded bg-slate-900 border p-2 text-slate-200 ${validTo ? "border-slate-600" : "border-red-500"}`} />
      </label>
      {!validTo && <div className="text-xs text-red-400">Неверный формат e-mail</div>}
      {r.subject_no && <details className="text-slate-400"><summary className="cursor-pointer">Оригинал (NO)</summary><pre className="whitespace-pre-wrap text-xs mt-1">{r.subject_no}{"\n\n"}{r.body_no}</pre></details>}
      <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full rounded bg-slate-900 border border-slate-600 text-slate-200 p-2" data-testid={`held-email-subject-${r.id}`} />
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="w-full rounded bg-slate-900 border border-slate-600 text-slate-200 p-2 text-xs" data-testid={`held-email-body-${r.id}`} />
      <div className="flex flex-wrap gap-2">
        <button disabled={busy || !validTo} onClick={() => act("send", { subject, body, recipient_email: to.trim() }, "Отправлено")}
          className="px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-600 text-white text-xs disabled:opacity-40" data-testid={`held-email-send-${r.id}`}>{busy ? "…" : "Проверено — отправить повторно"}</button>
        <button disabled={busy} onClick={() => act("acknowledge", {}, "Скрыто. Запись сохранена в истории")}
          className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-xs disabled:opacity-40" data-testid={`held-email-ack-${r.id}`}>Проверено — скрыть</button>
      </div>
    </div>
  );
}

const statusCell = (r) => (r.acknowledged_at ? `${r.status} · скрыто ${fmt(r.acknowledged_at)}` : r.status || "-");

export default function AdminNotificationHistory() {
  const [rows, setRows] = useState([]);
  const [params, setParams] = useSearchParams();
  const onlyProblems = params.get("problems") === "1";
  const load = () => http.get("/v1/admin/notification-history").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const problems = rows.filter(isProblem);
  const checked = rows.filter((r) => r.acknowledged_at && PROBLEM_LABEL[r.status]);
  const shown = onlyProblems ? rows.filter((r) => PROBLEM_LABEL[r.status]) : rows;
  const clearChecked = async () => {
    if (!window.confirm(`Удалить проверенные записи о проблемных письмах: ${checked.length}?\nЗаказы, клиенты и непроверенные проблемы не затрагиваются.`)) {
      return;
    }
    try { const r = await http.delete("/v1/admin/notifications/acknowledged"); toast.success(`Удалено: ${r.data.deleted}`); load(); } catch { toast.error("Ошибка"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-white">История уведомлений</h1>
        <div className="flex gap-2">
          <button onClick={clearChecked} disabled={checked.length === 0} data-testid="notif-clear-checked"
            className="px-3 py-1.5 rounded bg-red-800 hover:bg-red-700 text-white text-sm disabled:opacity-40">Очистить проверенные ({checked.length})</button>
          <button onClick={() => setParams(onlyProblems ? {} : { problems: "1" })} data-testid="notif-filter-toggle"
            className="px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm">{onlyProblems ? "Показать все" : "Только проблемы"}</button>
        </div>
      </div>
      <Panel title={`Требуют решения: ${problems.length}`}>
        {problems.length === 0 ? <p className="text-sm text-emerald-300" data-testid="notif-no-problems">Нет нерешённых проблем с письмами.</p> : (
          <div className="space-y-3">{problems.map((r) => <ProblemEmail key={r.id} r={r} onDone={load} />)}</div>
        )}
      </Panel>
      <Panel title={`${onlyProblems ? "Проблемные" : "Всего"}: ${shown.length}`}>
        {shown.length === 0 ? <Empty /> : (
          <Table head={["Получатель", "Тема", "Тип", "Статус", "Причина", "Дата"]}>
            {shown.map((r) => (
              <tr key={r.id} className="border-b border-slate-700/50" data-testid={`notif-row-${r.id}`}>
                <Td>{r.recipient_email}</Td>
                <Td>{r.subject}</Td>
                <Td>{r.trigger_type}</Td>
                <Td>{statusCell(r)}</Td>
                <Td>{r.error_message || "—"}</Td>
                <Td>{fmt(r.created_at)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
    </div>
  );
}
